import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress, Snackbar,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { apiService } from '../../services/apiService';

const UploadMasterTab = ({ observatoryId, observatoryName, onUploaded, onCancel }) => {
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // True once an archive's bytes are uploaded and the server is extracting/ingesting.
  const [processingArchive, setProcessingArchive] = useState(false);
  // Per-file results window for archive uploads (stays open until dismissed).
  const [archiveResults, setArchiveResults] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const handleUpload = async () => {
    if (!uploadFile || !observatoryId) return;
    const isArchive = /\.(zip|tar|tgz|gz)$/i.test(uploadFile.name || '');
    setUploading(true);
    setUploadProgress(0);
    setProcessingArchive(false);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('observatory_id', observatoryId);

      const resp = await apiService.uploadMasterFrame(formData, (progressEvent) => {
        const progress = progressEvent.total
          ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
          : 0;
        setUploadProgress(progress);
        // Once the bytes are in, the server is extracting + ingesting the archive.
        if (isArchive && progress >= 100) setProcessingArchive(true);
      });

      // Archive uploads return a per-file report { archive, added, skipped,
      // total, results[] }; single-file uploads return the frame record.
      const data = resp?.data;
      if (data && data.archive) {
        setArchiveResults({ added: data.added, skipped: data.skipped, total: data.total, results: data.results || [] });
      } else {
        setNotification({ open: true, message: 'Master frame uploaded successfully', severity: 'success' });
      }
      setUploadFile(null);
      onUploaded?.();
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Upload failed';
      setNotification({ open: true, message: typeof message === 'string' ? message : 'Upload failed', severity: 'error' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setProcessingArchive(false);
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Upload Master Calibration Frame</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Upload a pre-made master frame (dark, flat, or bias) as a single FITS file.
            Metadata is extracted from FITS headers automatically (IMAGETYP, GAIN,
            OFFSET, FILTER, EXPTIME, CCD-TEMP, etc.).
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Observatory: <strong>{observatoryName || '(none selected)'}</strong>
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            disabled={uploading || !observatoryId}
            fullWidth
            sx={{ py: 2, mb: 2 }}
          >
            {uploadFile ? uploadFile.name : 'Select FITS file (.fits, .fit, .fts, .fz)'}
            <input
              type="file"
              hidden
              accept=".fits,.fit,.fts,.fz"
              onChange={(e) => setUploadFile(e.target.files[0])}
            />
          </Button>
          {uploading && (
            processingArchive ? (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Processing Archive…
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress}% uploaded
                </Typography>
              </Box>
            )
          )}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setUploadFile(null);
                onCancel?.();
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!uploadFile || uploading || !observatoryId}
              startIcon={<UploadIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Archive upload results — stays open until the user dismisses it */}
      <Dialog
        open={!!archiveResults}
        onClose={() => setArchiveResults(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Archive Upload Results</DialogTitle>
        <DialogContent dividers>
          {archiveResults && (
            <>
              <Alert
                severity={archiveResults.skipped > 0 ? (archiveResults.added > 0 ? 'warning' : 'error') : 'success'}
                sx={{ mb: 2 }}
              >
                {archiveResults.added} added, {archiveResults.skipped} skipped of {archiveResults.total} file(s).
              </Alert>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell sx={{ width: 110 }}>Status</TableCell>
                      <TableCell>Detail</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {archiveResults.results.map((r, i) => (
                      <TableRow key={`${r.filename}-${i}`}>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{r.filename}</TableCell>
                        <TableCell>
                          {r.status === 'added' ? (
                            <Chip icon={<CheckCircleIcon />} label="Added" color="success" size="small" variant="outlined" />
                          ) : (
                            <Chip icon={<BlockIcon />} label="Skipped" color="default" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                          {r.detail || ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveResults(null)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {typeof notification.message === 'string' ? notification.message : String(notification.message ?? '')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UploadMasterTab;
